/**
 * What `ssh` stderr says went wrong.
 *
 * One classifier, used by both things that drive `ssh`: opening a forward to an Environment and
 * installing onto a computer. "This needs a password" has to mean the same in both, or a
 * destination would prompt during setup and fail silently on connect.
 *
 * Anything unrecognised is `unreachable`, which retries: guessing a host-key verdict from
 * unfamiliar output would park a connection a retry would have fixed.
 */
export type SshFailureKind =
  /** Never connected to this machine before: there is no key on record to compare against. */
  | "unknown-host-key"
  /** The key on record does not match the one offered. Answered by the person, never by a retry. */
  | "changed-host-key"
  | "authentication"
  | "unreachable";

/**
 * A changed key is checked for first and matched on its own wording.
 *
 * Both cases end with the same "Host key verification failed." line, so that line alone cannot
 * tell them apart — only the warning above it can, and reading it in the wrong order would file a
 * possible interception as an ordinary first contact.
 */
const CHANGED_HOST_KEY_PATTERNS = [
  "remote host identification has changed",
  "host key for",
  "host identification has changed",
];

const UNKNOWN_HOST_KEY_PATTERNS = [
  "host key verification failed",
  "no matching host key type found",
  // OpenSSH's wording when strict checking refuses a host it has no record of.
  "and you have requested strict checking",
  "no rsa host key is known",
  "no ecdsa host key is known",
  "no ed25519 host key is known",
];

const AUTHENTICATION_PATTERNS = [
  "permission denied",
  "too many authentication failures",
  "no supported authentication methods",
  "authentication failed",
  "publickey",
];

export function classifySshFailure(stderr: string): SshFailureKind {
  const text = stderr.toLowerCase();
  // Host key before authentication: offering a password prompt for a key problem would walk the
  // person straight past the warning that matters.
  if (CHANGED_HOST_KEY_PATTERNS.some((pattern) => text.includes(pattern)))
    return "changed-host-key";
  if (UNKNOWN_HOST_KEY_PATTERNS.some((pattern) => text.includes(pattern)))
    return "unknown-host-key";
  if (AUTHENTICATION_PATTERNS.some((pattern) => text.includes(pattern))) return "authentication";
  return "unreachable";
}

/**
 * Whether a failure means the destination refused the credential, and so is worth asking the
 * person for a password over. Reads whatever message the error carries, because the retry ladder
 * sees errors rather than raw stderr.
 */
export function isSshAuthFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return classifySshFailure(message) === "authentication";
}

/**
 * Whether a failure means this machine has never been connected to.
 *
 * Worth asking the person to confirm the destination's fingerprint over, which is the same
 * question `ssh` asks in a terminal on first contact. A *changed* key is deliberately not included:
 * that one is answered in their own terminal (ADR-0126).
 */
export function isUnknownHostKeyFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return classifySshFailure(message) === "unknown-host-key";
}
